import { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { fluxogramasService } from '@/services/fluxogramaApi';
import type { FluxogramaHistorico } from '@/types/fluxograma';
import { toast } from 'sonner';

interface HistoricoDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fluxogramaId: string;
}

const acaoLabel: Record<FluxogramaHistorico['acao'], string> = {
  criou: 'Criou',
  atualizou: 'Atualizou',
  excluiu: 'Excluiu',
  duplicou: 'Duplicou',
  arquivou: 'Arquivou',
  compartilhou: 'Compartilhou',
  revogou_link: 'Revogou link',
};

const entidadeLabel: Record<FluxogramaHistorico['entidade'], string> = {
  fluxograma: 'Fluxograma',
  node: 'Nó',
  edge: 'Conexão',
};

export function HistoricoDrawer({ open, onOpenChange, fluxogramaId }: HistoricoDrawerProps) {
  const [items, setItems] = useState<FluxogramaHistorico[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fluxogramasService
      .getHistorico(fluxogramaId)
      .then((res) => {
        if (res.success) setItems(res.data);
        else toast.error(res.error || 'Erro ao carregar histórico');
      })
      .catch(() => toast.error('Erro ao carregar histórico'))
      .finally(() => setLoading(false));
  }, [fluxogramaId, open]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[420px] sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Histórico de alterações</SheetTitle>
          <SheetDescription>Últimas 100 ações registradas neste fluxograma.</SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-3">
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum evento registrado ainda.</p>
          ) : (
            items.map((h) => (
              <div key={h.id} className="border rounded-lg p-3 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="secondary" className="text-[10px] uppercase">
                    {acaoLabel[h.acao] || h.acao}
                  </Badge>
                  <Badge variant="outline" className="text-[10px]">
                    {entidadeLabel[h.entidade] || h.entidade}
                  </Badge>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {h.createdAt ? new Date(h.createdAt).toLocaleString() : ''}
                  </span>
                </div>
                <div className="mt-2 leading-snug">{h.descricao}</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  por <span className="font-medium">{h.usuario_nome}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
